/**
 * ExporterAktionenTab — shared shell for ALL Exporter-type APs.
 *
 * The same UI ships for every exporter: search bar, follow-up tray,
 * Aktuell / Veraltet record lists, Versand-Cockpit on click, and the
 * Step 2a (PDF preview) / Step 2b (E-Mail composer) sub-views. APs only
 * supply *data bindings* (renderPreviewHtml / doExport / fetchFreshness)
 * and a one-time defaults seed for the per-AP cockpit settings.
 *
 * Per-AP UX *flags* like exportButtonLabel, searchPlaceholder, showEmailBar,
 * cockpitEnabled etc. were removed on purpose — anything that affects look
 * & feel must live in the settings store, not the config object.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  IconAdjustments,
  IconAlertTriangle,
  IconBolt,
  IconCalendar,
  IconCheck,
  IconChevronLeft,
  IconMail,
  IconRobot,
  IconSearch,
  IconX,
} from "@tabler/icons-react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { api } from "../../../api/client"
import { getApSimulation } from "../../../lib/ap-simulations"
import { useStore } from "../../../store/useStore"
import type { QmsRecord, ScopeDiffState } from "../../../types"
import { IntegrationQueueTray } from "./IntegrationQueueTray"
import { ApStatusTile } from "./exporter-cockpit/ApStatusTile"
import {
  STATE_DISPLAY,
  VersandCockpit,
} from "./exporter-cockpit/VersandCockpit"
import { VersandComposer } from "./exporter-cockpit/VersandComposer"
import type {
  ExportOperation,
  ExportOpState,
} from "./exporter-cockpit/exportOperationsStore"
import {
  recordCurrentState,
  useExporterIntegrationStatus,
  useExportOperationsStore,
  useExportOperationsSync,
} from "./exporter-cockpit/exportOperationsStore"
import { applyDefaultsFromConfig } from "./exporter-cockpit/exporterSettingsStore"

// ── Public config contract ────────────────────────────────────────────────────

export type ExporterSearchResult = {
  record_id: string
  display_name: string
  matches: Array<{
    scope_id: string
    primitive_title: string
    value: string
  }>
  extra_match_count?: number
}

export type ExporterFreshnessData = {
  fresh_record_ids: string[]
  stale_record_ids: string[]
  stalest_relation_per_record: Record<
    string,
    {
      scope_id: string
      record_id?: string
      updated_at: string | null
      demotion_reason?: string
      demotion_source_ref?: {
        monitor_ap_id?: string
        finding_id?: string
      } | null
    } | null
  >
  // Importer-cascade: records whose upstream chain has an open Importer
  // PendingDocument. They are excluded from Bereit (auto-filter) and shown
  // in Inaktiv with the supplied reason — see temp.md "Staleness Cascading
  // via the Importer Lifecycle".
  pending_inaktiv_record_ids?: string[]
  pending_reason_per_record?: Record<
    string,
    {
      source_scope_id: string
      source_record_id: string
      source_record_name: string
      ap_id: string
    }
  >
}

/**
 * ExporterTabConfig — *only* data bindings + content per AP.
 *
 * The shell renders the same UI for every exporter (search bar always present,
 * uniform "Exportieren" button, Versand-Cockpit always on). Per-AP defaults
 * (subject / body / reminder days / reply-to) live in `useExporterSettingsStore`
 * and are seeded once via `applyDefaultsFromConfig`. Anything that affects how
 * the workspace looks should NOT be added here — extend the settings store.
 */
export interface ExporterTabConfig {
  apId: string
  heading: string
  subheading: string
  scopeId: string
  /** Plural label for records shown in section headers, e.g. "Kunden" or "Zulieferer". */
  recordLabel: string
  /**
   * Returns the freshness partition for the current threshold.
   * Also receives the already-loaded records so scope-local implementations
   * can compute staleness from record.updated_at without an extra API round-trip.
   */
  fetchFreshness: (
    threshold: string,
    records: QmsRecord[],
  ) => Promise<ExporterFreshnessData>
  /**
   * Returns rendered HTML for the export preview.
   * Receives the current diff-view state so the preview reflects any manual edits.
   */
  renderPreviewHtml: (
    recordId: string,
    diffs: Record<string, ScopeDiffState> | undefined,
  ) => Promise<string | null>
  /**
   * Executes the export (download, activity log, etc.).
   * Called with the latest diff-view state at the moment the user clicks "Exportieren".
   */
  doExport: (
    recordId: string,
    diffs: Record<string, ScopeDiffState> | undefined,
  ) => Promise<void>
  /** If provided, called before the export preview opens (e.g. to seed the diff view). */
  onStartExport?: (recordId: string) => Promise<void>
  /**
   * Optional override for cross-scope full-text search. If omitted, the shell
   * uses a built-in search across the loaded records' display name + values.
   */
  search?: (query: string) => Promise<ExporterSearchResult[]>
  /**
   * Templates available for dispatch. Defaults to a single "Standard-Vorlage"
   * if not provided. Plural from day one — Phase A typically uses length 1.
   */
  cockpitTemplates?: { id: string; title: string }[]

  // ── Initial settings seed (only applied once, then user-editable) ──────────
  // These are *not* read at render time. They feed `applyDefaultsFromConfig`
  // which writes them into useExporterSettingsStore on first mount of the AP,
  // after which the user controls them via APSettingsPage.
  defaultFollowUpDays?: number
  apOwnEmail?: string
  bodyTemplateDefault?: string
  subjectTemplateDefault?: string
}

/** Default template used when an AP doesn't configure its own. */
const DEFAULT_TEMPLATES: { id: string; title: string }[] = [
  { id: "default", title: "Standard Template" },
]

function getTemplates(
  config: ExporterTabConfig,
): { id: string; title: string }[] {
  return config.cockpitTemplates && config.cockpitTemplates.length > 0
    ? config.cockpitTemplates
    : DEFAULT_TEMPLATES
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const SCOPE_LABEL_KEYS: Record<
  string,
  Parameters<ReturnType<typeof useTranslation>["translate"]>[0]
> = {
  supplier: "QMS/EXPORTER/AP_SUPPLIER_SCOPE",
  customer: "QMS/EXPORTER/SCOPE_CUSTOMER",
  product: "QMS/EXPORTER/SCOPE_PRODUCT",
  component: "QMS/EXPORTER/SCOPE_COMPONENT",
  substance: "QMS/EXPORTER/SCOPE_SUBSTANCE",
}

function scopeLabel(
  scopeId: string,
  translate: ReturnType<typeof useTranslation>["translate"],
): string {
  const key = SCOPE_LABEL_KEYS[scopeId]
  return key ? translate(key) : scopeId
}

// Per the AP registry — the Importer/Exporter AP id used in PendingDocument.ap_id
// maps to the translation key of its human-readable name. Kept here as a flat
// map so the cascade reason renders without crossing the registry boundary
// (which would re-introduce a client-only display dependency in this shell).
const AP_DISPLAY_NAME_KEYS: Record<
  string,
  Parameters<ReturnType<typeof useTranslation>["translate"]>[0]
> = {
  supplier: "QMS/EXPORTER/AP_SUPPLIER",
  "customer-conformity": "QMS/EXPORTER/AP_CUSTOMER_CONFORMITY",
  "lieferanten-abfragen": "QMS/AP/SUPPLIER_INQUIRY/NAME",
  "lieferanten-tracking": "QMS/LIEFERANTEN_TRACKING/TITLE",
  "substanz-radar": "QMS/AP/SUBSTANZ_RADAR/NAME",
}

/** Builds the "downstream-pending" reason line shown on Inaktiv rows
 *  while an Importer is processing an upstream refresh. Matches temp.md:
 *  "Lieferant AURUBIS AG: Lieferanten Deklaration in Bearbeitung." */
function formatPendingReason(
  reason: {
    source_scope_id: string
    source_record_name: string
    ap_id: string
  },
  translate: ReturnType<typeof useTranslation>["translate"],
): string {
  const apName = AP_DISPLAY_NAME_KEYS[reason.ap_id]
    ? translate(AP_DISPLAY_NAME_KEYS[reason.ap_id])
    : reason.ap_id
  return `${scopeLabel(reason.source_scope_id, translate)} ${reason.source_record_name}: ${apName} ${translate("QMS/EXPORTER/PENDING_IN_PROGRESS")}`
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleDateString("de-DE")
  } catch {
    return iso
  }
}

function highlightMatch(value: string, query: string): React.ReactNode {
  if (!query) return value
  const idx = value.toLowerCase().indexOf(query.toLowerCase())
  if (idx < 0) return value
  return (
    <>
      {value.slice(0, idx)}
      <mark className="rounded bg-yellow1/30 px-0.5 text-yellow1">
        {value.slice(idx, idx + query.length)}
      </mark>
      {value.slice(idx + query.length)}
    </>
  )
}

// ── SearchBar ─────────────────────────────────────────────────────────────────

/**
 * Built-in fallback search. Filters loaded records by display name +
 * primitive values containing the query. Used when `config.search` is absent.
 */
function defaultLocalSearch(
  records: QmsRecord[],
  scopeId: string,
  query: string,
): ExporterSearchResult[] {
  const q = query.trim().toLowerCase()
  if (q.length < 2) return []
  const out: ExporterSearchResult[] = []
  for (const r of records) {
    const displayName =
      r.values[`${scopeId}_name`]?.value ??
      r.values["name"]?.value ??
      r.values[`${scopeId}_id`]?.value ??
      r.id
    const matches: ExporterSearchResult["matches"] = []
    if (displayName.toLowerCase().includes(q)) {
      matches.push({
        scope_id: scopeId,
        primitive_title: "Name",
        value: displayName,
      })
    }
    for (const [primName, cell] of Object.entries(r.values)) {
      if (matches.length >= 3) break
      const v = cell?.value
      if (typeof v !== "string") continue
      if (!v.toLowerCase().includes(q)) continue
      matches.push({
        scope_id: scopeId,
        primitive_title: primName,
        value: v,
      })
    }
    if (matches.length === 0) continue
    out.push({
      record_id: r.id,
      display_name: displayName,
      matches,
      extra_match_count: 0,
    })
    if (out.length >= 25) break
  }
  return out
}

function SearchBar({
  config,
  records,
  selectedIds,
  staleIds,
  onToggleRecord,
  onSwitchToCustom,
}: {
  config: ExporterTabConfig
  records: QmsRecord[]
  selectedIds: Set<string>
  staleIds: Set<string>
  onToggleRecord: (id: string) => void
  onSwitchToCustom: () => void
}) {
  const { translate } = useTranslation()
  const [query, setQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedQuery(query), 300)
  }, [query])

  const customSearch = config.search
  const recordCount = records.length
  const { data: results = [], isFetching } = useQuery({
    queryKey: [
      "exporter-search",
      config.apId,
      debouncedQuery,
      customSearch ? "remote" : "local",
      config.scopeId,
      recordCount,
      customSearch,
      records,
    ],
    queryFn: () =>
      customSearch
        ? customSearch(debouncedQuery)
        : Promise.resolve(
            defaultLocalSearch(records, config.scopeId, debouncedQuery),
          ),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  })

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  const handleSelect = (result: ExporterSearchResult) => {
    const isStale = staleIds.has(result.record_id)
    onToggleRecord(result.record_id)
    if (isStale) onSwitchToCustom()
    inputRef.current?.focus()
  }

  const showDropdown = open && debouncedQuery.length >= 2

  return (
    <div ref={containerRef} className="flex items-center justify-center py-6">
      <div className="relative w-full max-w-sm">
        <div
          className={clsx(
            "flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 transition-colors",
            open
              ? "border-yellow1/50 bg-grey1 shadow-xs shadow-yellow1/10"
              : "border-grey4 bg-grey1 hover:border-grey5",
          )}
        >
          <IconSearch
            size={15}
            className={clsx("shrink-0", open ? "text-yellow1" : "text-grey5")}
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            placeholder={translate("QMS/EXPORTER/SEARCH_HINT", {
              recordLabel: config.recordLabel,
            })}
            className="flex-1 bg-transparent text-sm text-white placeholder-grey5 outline-hidden"
          />
          {isFetching && (
            <div className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border border-grey4 border-t-yellow1" />
          )}
          {query && (
            <button
              onClick={() => {
                setQuery("")
                setDebouncedQuery("")
                setOpen(false)
              }}
              className="shrink-0 text-grey5 hover:text-white"
            >
              <IconX size={13} />
            </button>
          )}
        </div>
        <p className="mt-1.5 text-center text-[11px] text-grey5">
          {translate("QMS/EXPORTER/SEARCH_HINT", {
            recordLabel: config.recordLabel,
          })}
        </p>

        {showDropdown && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-lg border border-grey4 bg-grey1 shadow-xl shadow-black/40">
            {results.length === 0 && !isFetching && (
              <p className="px-4 py-3 text-xs italic text-grey5">
                {translate("QMS/EXPORTER/NO_RESULTS", {
                  query: debouncedQuery,
                })}
              </p>
            )}
            {results.map((result) => {
              const isSelected = selectedIds.has(result.record_id)
              const isStale = staleIds.has(result.record_id)
              const topMatch = result.matches[0]

              return (
                <button
                  key={result.record_id}
                  onClick={() => handleSelect(result)}
                  className="flex w-full items-start gap-3 border-b border-grey4/40 px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-grey3/40"
                >
                  <div
                    className={clsx(
                      "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                      isSelected
                        ? "border-yellow1 bg-yellow1/20"
                        : "border-grey4 bg-grey2",
                    )}
                  >
                    {isSelected && (
                      <IconCheck size={10} className="text-yellow1" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-white">
                        {result.display_name}
                      </span>
                      {isStale && (
                        <span className="bg-amber-500/15 text-amber-400 border-amber-500/30 shrink-0 rounded border px-1 py-0 text-[10px] font-medium">
                          {translate("QMS/EXPORTER/BADGE_STALE")}
                        </span>
                      )}
                      {isSelected && !isStale && (
                        <span className="bg-green-500/15 text-green-400 border-green-500/30 shrink-0 rounded border px-1 py-0 text-[10px] font-medium">
                          {translate("QMS/EXPORTER/BADGE_SELECTED")}
                        </span>
                      )}
                    </div>
                    {topMatch && (
                      <p className="mt-0.5 text-xs text-grey5">
                        <span className="font-medium text-grey6">
                          {scopeLabel(topMatch.scope_id, translate)}
                        </span>
                        {" · "}
                        <span>{topMatch.primitive_title}</span>
                        {" · "}
                        <span className="font-mono">
                          {highlightMatch(topMatch.value, debouncedQuery)}
                        </span>
                        {(result.extra_match_count ?? 0) > 0 && (
                          <span className="ml-1 text-grey5/70">
                            {translate("QMS/EXPORTER/EXTRA_MATCHES", {
                              count: result.extra_match_count ?? 0,
                            })}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ── SelectionModeBar ──────────────────────────────────────────────────────────

function SelectionModeBar({
  mode,
  threshold,
  onThresholdChange,
  onSwitchToFreshness,
}: {
  mode: "freshness" | "custom"
  threshold: string
  onThresholdChange: (v: string) => void
  onSwitchToFreshness: () => void
}) {
  const { translate } = useTranslation()
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 pb-3">
      <div className="flex items-center gap-1.5 rounded-full border border-grey4 bg-grey2 px-3 py-1">
        <IconCalendar size={11} className="shrink-0 text-grey5" />
        <input
          type="date"
          value={threshold}
          onChange={(e) => onThresholdChange(e.target.value)}
          className="w-28 bg-transparent text-xs text-white outline-hidden"
        />
      </div>
      {mode === "freshness" ? (
        <span className="border-green-500/30 bg-green-500/10 text-green-400 rounded-full border px-2.5 py-1 text-[11px]">
          {translate("QMS/EXPORTER/FILTER_ACTIVE")}
        </span>
      ) : (
        <>
          <span className="rounded-full border border-yellow1/30 bg-yellow1/10 px-2.5 py-1 text-[11px] text-yellow1">
            <IconAdjustments size={10} className="-mt-px mr-1 inline" />
            {translate("QMS/EXPORTER/CUSTOM_SELECTION")}
          </span>
          <button
            onClick={onSwitchToFreshness}
            className="text-[11px] text-grey5 transition-colors hover:text-white"
          >
            {translate("QMS/EXPORTER/BACK_TO_FILTER")}
          </button>
        </>
      )}
    </div>
  )
}

// ── RecordRow ─────────────────────────────────────────────────────────────────

interface RecordRowProps {
  rec: QmsRecord
  displayName: string
  mode: "aktuell" | "veraltet"
  isStaleInCustomMode?: boolean
  staleHint?: {
    scope_id: string
    updated_at: string | null
    demotion_reason?: string
  } | null
  /** Effective state of the record's latest export operation, if any. */
  currentState: ExportOpState | null
  /** Whole-row click — opens the Versand-Cockpit for this record. */
  onOpen: () => void
  onDemote?: () => void
}

const STATE_LABEL_KEYS: Record<
  ExportOpState,
  Parameters<ReturnType<typeof useTranslation>["translate"]>[0]
> = {
  draft: "QMS/COCKPIT/STATE_DRAFT",
  answered_ok: "QMS/COCKPIT/STATE_ANSWERED_OK",
  answered_followup: "QMS/COCKPIT/STATE_ANSWERED_FOLLOWUP",
  answered_received: "QMS/COCKPIT/STATE_ANSWERED_RECEIVED",
  waiting: "QMS/COCKPIT/STATE_WAITING",
  due: "QMS/COCKPIT/STATE_DUE",
  closed: "QMS/COCKPIT/STATE_CLOSED",
}

function StateBadge({ state }: { state: ExportOpState }) {
  const { translate } = useTranslation()
  const meta = STATE_DISPLAY[state]
  const ringByState: Record<ExportOpState, string> = {
    draft: "border-purple-500/40 bg-purple-500/10",
    answered_ok: "border-green-500/30 bg-green-500/10",
    answered_followup: "border-orange-500/40 bg-orange-500/10",
    answered_received: "border-blue-500/40 bg-blue-500/10",
    waiting: "border-yellow1/40 bg-yellow1/10",
    due: "border-red-500/40 bg-red-500/10",
    closed: "border-grey4 bg-grey3/40",
  }
  return (
    <span
      className={clsx(
        "shrink-0 whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] font-medium",
        ringByState[state],
        meta.cls,
      )}
      title={translate("QMS/EXPORTER/STATE_BADGE_PREFIX", {
        label: translate(STATE_LABEL_KEYS[state]),
      })}
    >
      {translate(STATE_LABEL_KEYS[state])}
    </span>
  )
}

function RecordRow({
  displayName,
  mode,
  isStaleInCustomMode,
  staleHint,
  currentState,
  onOpen,
  onDemote,
}: RecordRowProps) {
  const { translate } = useTranslation()
  // Whole row is the trigger — opens the Versand-Cockpit. The dedicated
  // "Exportieren" button has been removed; the export flow always begins
  // with the cockpit. Detail panel is reachable from there.
  if (mode === "aktuell") {
    return (
      <div
        onClick={onOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            onOpen()
          }
        }}
        title={translate("QMS/EXPORTER/TOOLTIP_OPEN_COCKPIT")}
        className="group flex cursor-pointer items-center gap-2 border-b border-grey4/40 px-3 py-2.5 transition-colors last:border-b-0 hover:bg-grey3/30 focus:bg-grey3/30 focus:outline-hidden"
      >
        <IconCheck size={14} className="text-green-400 shrink-0" />
        <span className="flex-1 truncate text-sm text-white">
          {displayName}
        </span>

        {currentState && <StateBadge state={currentState} />}

        {onDemote && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDemote()
            }}
            className={clsx(
              "shrink-0 rounded p-0.5 text-grey5 transition-all hover:text-white",
              isStaleInCustomMode
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100",
            )}
            title={translate("QMS/EXPORTER/BTN_REMOVE_FROM_READY")}
          >
            <IconX size={13} />
          </button>
        )}
      </div>
    )
  }

  const hintText = staleHint?.demotion_reason
    ? staleHint.demotion_reason
    : staleHint
      ? `${scopeLabel(staleHint.scope_id, translate)} · ${formatDate(staleHint.updated_at)}`
      : null

  return (
    <div
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onOpen()
        }
      }}
      title={translate("QMS/EXPORTER/TOOLTIP_OPEN_COCKPIT")}
      className="flex w-full cursor-pointer items-center gap-3 border-b border-grey4/40 px-3 py-2.5 opacity-70 transition-all last:border-b-0 hover:bg-grey3/30 hover:opacity-100 focus:bg-grey3/30 focus:opacity-100 focus:outline-hidden"
    >
      <IconAlertTriangle size={13} className="text-orange-400 shrink-0" />
      <span className="flex-1 truncate text-sm text-grey6">{displayName}</span>
      {currentState && <StateBadge state={currentState} />}
      {hintText && (
        <span
          className={clsx(
            "max-w-[180px] shrink-0 truncate whitespace-nowrap text-[11px]",
            staleHint?.demotion_reason ? "text-yellow1/70" : "text-grey5",
          )}
        >
          {hintText}
        </span>
      )}
    </div>
  )
}

// ── ExportPreviewView ─────────────────────────────────────────────────────────

function ExportPreviewView({
  config,
  recordId,
  recordName,
  onVerwerfen,
  onExportieren,
  isExporting,
  readOnly = false,
}: {
  config: ExporterTabConfig
  recordId: string
  recordName: string
  onVerwerfen: () => void
  onExportieren: () => void
  isExporting: boolean
  /** Historic-case view: hides Exportieren, locks down the preview footer message. */
  readOnly?: boolean
}) {
  const { translate } = useTranslation()
  const { actionState } = useStore()
  const [debouncedDiffs, setDebouncedDiffs] = useState<
    Record<string, ScopeDiffState> | undefined
  >(undefined)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(
      () => setDebouncedDiffs(actionState?.diffs),
      300,
    )
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [actionState])

  const { data: previewHtml, isLoading } = useQuery({
    queryKey: ["exporter-preview", config.apId, recordId, debouncedDiffs],
    queryFn: () => config.renderPreviewHtml(recordId, debouncedDiffs),
    staleTime: 0,
  })

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-grey4 px-4 py-3">
        <button
          onClick={onVerwerfen}
          className="flex items-center gap-1 text-xs text-grey5 transition-colors hover:text-white"
        >
          <IconChevronLeft size={14} />
          {translate("QMS/COMMON/BACK")}
        </button>
        <span className="mx-1 text-xs text-grey5">|</span>
        <span className="truncate text-sm font-medium text-white">
          {readOnly
            ? translate("QMS/EXPORTER/PDF_PREVIEW_TITLE")
            : translate("QMS/CUSTOMER/EXPORT")}{" "}
          · {recordName}
        </span>
        {readOnly && (
          <span className="rounded border border-grey4/60 bg-grey3/40 px-1.5 py-0.5 text-[10px] text-grey5">
            {translate("QMS/EXPORTER/PREVIEW_BADGE_READONLY")}
          </span>
        )}
      </div>

      <div className="relative flex-1 overflow-hidden">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-grey2/80">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-grey4 border-t-yellow1" />
          </div>
        )}
        {previewHtml ? (
          <iframe
            srcDoc={previewHtml}
            className="h-full w-full border-0 bg-white"
            sandbox="allow-same-origin"
            title={translate("QMS/EXPORTER/PREVIEW_IFRAME_TITLE")}
          />
        ) : (
          !isLoading && (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-grey5">
                {translate("QMS/EXPORTER/PREVIEW_LOADING")}
              </p>
            </div>
          )
        )}
      </div>

      <p className="border-t border-grey4 px-4 py-1.5 text-xs text-grey5">
        {readOnly
          ? translate("QMS/EXPORTER/PREVIEW_FOOTER_READONLY")
          : translate("QMS/EXPORTER/PREVIEW_FOOTER_EDIT")}
      </p>

      <div className="flex items-center justify-end gap-3 border-t border-grey4 px-4 py-3">
        <button
          onClick={onVerwerfen}
          disabled={isExporting}
          className="rounded-lg border border-grey4 px-4 py-2 text-sm text-grey5 transition-colors hover:border-grey5 hover:text-white disabled:opacity-50"
        >
          {readOnly
            ? translate("QMS/EXPORTER/BTN_CLOSE")
            : translate("QMS/EXPORTER/BTN_DISCARD")}
        </button>
        <button
          onClick={onExportieren}
          disabled={isExporting || !previewHtml || readOnly}
          title={
            readOnly
              ? translate("QMS/EXPORTER/TOOLTIP_ALREADY_EXPORTED")
              : undefined
          }
          className="hover:bg-yellow-300 rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-grey1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isExporting
            ? translate("QMS/EXPORTER/BTN_EXPORTING")
            : readOnly
              ? translate("QMS/EXPORTER/BTN_ALREADY_EXPORTED")
              : translate("QMS/CUSTOMER/EXPORT")}
        </button>
      </div>
    </div>
  )
}

// ── AusstehendRow ─────────────────────────────────────────────────────────────
//
// One row of the Ausstehend section — the "needs human action" partition
// half. Per the one-record-one-state contract (a record has at most one
// open work item per AP, transitioning through sources/reasons over time),
// this row shows the *current lifecycle state* of the record, not just the
// trigger event that opened the work item:
//
//   - Befund applied, no operation yet → "Empfehlung — Fragebogen senden"
//   - Operation waiting                → "Versendet — wartet auf Antwort"
//   - Operation due (overdue)          → "Frist überschritten — Erinnerung"
//   - Reply landed (answered_received) → "Antwort eingegangen — Übergeben?"
//
// Clicking opens the Versand-Cockpit so the user can advance the state.

const AUSSTEHEND_SOURCE_META: Record<
  string,
  { icon: typeof IconBolt; label: string }
> = {
  monitor: { icon: IconRobot, label: "Monitor" },
  cross_ap: { icon: IconBolt, label: "Other AP" },
  email: { icon: IconMail, label: "E-Mail Reply" },
  webhook: { icon: IconBolt, label: "Webhook" },
  user_manual: { icon: IconBolt, label: "Manual" },
  auto_reminder: { icon: IconBolt, label: "Reminder" },
}

function AusstehendRow({
  displayName,
  source,
  reason,
  priority,
  currentOpState,
  onOpen,
}: {
  displayName: string
  source: string
  reason: string
  priority: "high" | "normal"
  currentOpState: ExportOpState | null
  onOpen: () => void
}) {
  const { translate } = useTranslation()
  const meta =
    AUSSTEHEND_SOURCE_META[source] ?? AUSSTEHEND_SOURCE_META.user_manual
  const Icon = meta.icon

  const SOURCE_LABEL_MAP: Record<string, Parameters<typeof translate>[0]> = {
    monitor: "QMS/EXPORTER/SOURCE_MONITOR",
    cross_ap: "QMS/EXPORTER/SOURCE_OTHER_AP",
    email: "QMS/EXPORTER/SOURCE_EMAIL",
    webhook: "QMS/EXPORTER/SOURCE_WEBHOOK",
    user_manual: "QMS/EXPORTER/SOURCE_MANUAL",
    auto_reminder: "QMS/EXPORTER/SOURCE_REMINDER",
  }

  function getStatusLine(): string {
    switch (currentOpState) {
      case "waiting":
        return translate("QMS/EXPORTER/STATUS_WAITING")
      case "due":
        return translate("QMS/EXPORTER/STATUS_OVERDUE")
      case "answered_received":
        return translate("QMS/EXPORTER/STATUS_REPLY_RECEIVED")
      case "answered_ok":
        return translate("QMS/EXPORTER/STATUS_TRANSFERRED")
      case "answered_followup":
        return translate("QMS/EXPORTER/STATUS_FOLLOWUP_SENT")
      case "closed":
        return translate("QMS/EXPORTER/STATUS_CLOSED")
      default:
        break
    }
    if (source === "monitor" || source === "cross_ap")
      return translate("QMS/EXPORTER/STATUS_RECOMMEND")
    if (source === "email")
      return reason || translate("QMS/COCKPIT/STATE_ANSWERED_RECEIVED")
    if (source === "auto_reminder")
      return reason || translate("QMS/EXPORTER/STATUS_OVERDUE")
    return reason || translate("QMS/EXPORTER/STATUS_ACTION_REQUIRED")
  }

  const status = getStatusLine()
  const sourceLabel = SOURCE_LABEL_MAP[source]
    ? translate(SOURCE_LABEL_MAP[source])
    : meta.label
  return (
    <div
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onOpen()
        }
      }}
      title={translate("QMS/EXPORTER/TOOLTIP_OPEN_COCKPIT")}
      className="flex cursor-pointer items-start gap-2.5 border-b border-yellow1/15 px-3 py-2.5 transition-colors last:border-b-0 hover:bg-yellow1/6 focus:outline-hidden"
    >
      <Icon size={14} className="mt-0.5 shrink-0 text-yellow1" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-white">
            {displayName}
          </span>
          {currentOpState && <StateBadge state={currentOpState} />}
          {priority === "high" && !currentOpState && (
            <span className="shrink-0 rounded border border-yellow1/40 bg-yellow1/20 px-1 py-0 text-[10px] font-medium text-yellow1">
              {translate("QMS/EXPORTER/PRIORITY_HIGH")}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[11px] text-grey5">
          <span className="font-medium text-grey6">{sourceLabel}</span>
          {" · "}
          <span>{status}</span>
        </p>
      </div>
    </div>
  )
}

// ── Main shell ────────────────────────────────────────────────────────────────

export function ExporterAktionenTab({ config }: { config: ExporterTabConfig }) {
  const { translate } = useTranslation()
  const {
    exporterThreshold,
    setExportThreshold,
    activeExportRecordId,
    setActiveExport,
    clearActiveExport,
    selectedRecordIds,
    clearSelection,
    selectRecords,
    deselectRecords,
    openRecordDetail,
    cancelAction,
    exporterSelectionMode,
    setExportSelectionMode,
  } = useStore()

  const [isExporting, setIsExporting] = useState(false)
  const queryClient = useQueryClient()

  // Cockpit sub-mode state — only used when config.cockpitEnabled is true.
  const [cockpitView, setCockpitView] = useState<
    "cockpit" | "preview" | "composer"
  >("cockpit")
  const [composerCtx, setComposerCtx] = useState<{
    templateIds: string[]
    followUpOf?: { id: string; reason?: string }
    /** Set when re-opening a historic case → render read-only, prefilled from the op. */
    historicOp?: ExportOperation
    /** Set when continuing a saved draft — composer is editable, prefilled from the draft. */
    draftOp?: ExportOperation
  } | null>(null)

  const addOperation = useExportOperationsStore((s) => s.add)
  const removeDraft = useExportOperationsStore((s) => s.removeDraft)
  const simulateReplyOp = useExportOperationsStore((s) => s.simulateReply)
  const setReplyDetectionOpId = useExportOperationsStore(
    (s) => s.setReplyDetectionOpId,
  )
  const integration = useExporterIntegrationStatus()

  // Hydrate + poll the operations store for this AP. The polling interval
  // is what surfaces inbound replies (no websocket layer in this phase).
  useExportOperationsSync(config.apId)

  // Seed per-AP cockpit settings on first mount. After this initial write,
  // the user owns these via APSettingsPage — config defaults are not re-read.
  useEffect(() => {
    applyDefaultsFromConfig(config.apId, {
      apOwnEmail: config.apOwnEmail,
      bodyTemplateDefault: config.bodyTemplateDefault,
      subjectTemplateDefault: config.subjectTemplateDefault,
      defaultFollowUpDays: config.defaultFollowUpDays,
    })
  }, [
    config.apId,
    config.apOwnEmail,
    config.bodyTemplateDefault,
    config.subjectTemplateDefault,
    config.defaultFollowUpDays,
  ])

  // Reset cockpit sub-view whenever the active record changes.
  useEffect(() => {
    if (!activeExportRecordId) {
      setCockpitView("cockpit")
      setComposerCtx(null)
    }
  }, [activeExportRecordId])

  // Drop any active record selection on AP entry. activeExportRecordId is a
  // single global field shared across exporter APs, so without this a record
  // chosen in one exporter (e.g. Aurubis in Lieferanten-Abfragen) would auto-
  // open the cockpit when the user later visits a different exporter.
  useEffect(() => {
    clearActiveExport()
    // intentionally fires only on AP entry / change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.apId])

  // Reset the AP card's notification badge to 0 on mount. The work items
  // themselves stay in the Ausstehend bucket — only the *unread* counter
  // resets. Server-side persistence (ApSettings.last_opened_at) means the
  // reset survives reloads and is shared across tabs/devices.
  useEffect(() => {
    api
      .markApOpened(config.apId)
      .then(() =>
        queryClient.invalidateQueries({
          queryKey: ["ap-notifications-summary"],
        }),
      )
      .catch(() => {
        /* best-effort */
      })
    // The landing page's "neu frisch geworden" badge for this AP uses a
    // local-only lastOpenedAt baseline (see AutoPilotsLanding's
    // customerNewFreshCount). Without bumping it here, opening the AP
    // never clears the badge.
    try {
      localStorage.setItem(
        `qms.ap.lastOpenedAt.${config.apId}`,
        String(Date.now()),
      )
    } catch {
      // best-effort — localStorage may be unavailable in some embeds
    }
  }, [config.apId, queryClient])

  const { data: recordsPage } = useQuery({
    queryKey: ["records", config.scopeId],
    queryFn: () => api.listRecords(config.scopeId),
    staleTime: 30_000,
  })
  const allRecords = useMemo(() => recordsPage?.records ?? [], [recordsPage])
  const recordById = useMemo(
    () => Object.fromEntries(allRecords.map((r) => [r.id, r])),
    [allRecords],
  )
  const allRecordIds = useMemo(() => allRecords.map((r) => r.id), [allRecords])

  // Per-record current state for inline badges. Recomputed when operations
  // change; selectors over a small in-memory array, so cost is negligible.
  const exportOperations = useExportOperationsStore((s) => s.operations)
  const currentStateByRecord = useMemo(() => {
    const map: Record<string, ExportOpState | null> = {}
    for (const id of allRecordIds) {
      const cur = recordCurrentState(exportOperations, config.apId, id)
      map[id] = cur ? cur.state : null
    }
    return map
  }, [exportOperations, config.apId, allRecordIds])

  const { data: freshness } = useQuery({
    queryKey: [
      "freshness",
      config.apId,
      exporterThreshold,
      allRecords.length,
      allRecords,
    ],
    queryFn: () => config.fetchFreshness(exporterThreshold, allRecords),
    enabled: allRecords.length > 0,
    staleTime: 60_000,
  })

  // M3: unified partition (Ausstehend / Bereit / Inaktiv). Drives the new
  // top-of-tab signal section without disturbing the existing freshness-
  // based selection bookkeeping below — those still hold the export
  // selection state and seed the search bar's "stale" badges. The Ausstehend
  // bucket here is purely additive: open APWorkItems on this AP. M5 will
  // collapse the freshness query into this one.
  const { data: partition } = useQuery({
    queryKey: ["ap-partition", config.apId, exporterThreshold],
    queryFn: () => api.apPartition(config.apId, exporterThreshold),
    enabled: allRecords.length > 0,
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const staleIds = useMemo(
    () => new Set(freshness?.stale_record_ids ?? []),
    [freshness],
  )

  // Importer-cascade: records whose upstream chain has an open PendingDocument.
  // They are excluded from the Bereit auto-filter and surfaced in Inaktiv with
  // the cascade reason — temp.md "Staleness Cascading via the Importer Lifecycle".
  const pendingInaktivIds = useMemo(
    () => new Set(freshness?.pending_inaktiv_record_ids ?? []),
    [freshness],
  )
  const effectiveFreshIds = useMemo(
    () =>
      (freshness?.fresh_record_ids ?? []).filter(
        (id) => !pendingInaktivIds.has(id),
      ),
    [freshness, pendingInaktivIds],
  )

  // Sync selection with freshness only in freshness mode.
  //
  // Re-syncs whenever the current selection drifts from `effectiveFreshIds`.
  // Two triggers matter: (a) freshness data updated (threshold change, refetch)
  // and (b) selection was externally cleared — e.g. AutoPilotsLanding clears
  // the global selection on its mount (importer-AP hygiene). Without (b),
  // navigating cockpit → landing → AP would leave selection empty and render
  // every record as "veraltet" until a hard reload, because the cached
  // `freshness` reference doesn't change and the old "fire only on freshness
  // change" effect never re-ran.
  useEffect(() => {
    if (!freshness || activeExportRecordId) return
    if (exporterSelectionMode !== "freshness") return
    const matches =
      selectedRecordIds.size === effectiveFreshIds.length &&
      effectiveFreshIds.every((id) => selectedRecordIds.has(id))
    if (matches) return
    clearSelection()
    if (effectiveFreshIds.length > 0) selectRecords(effectiveFreshIds)
  }, [
    freshness,
    exporterSelectionMode,
    effectiveFreshIds,
    selectedRecordIds,
    activeExportRecordId,
    clearSelection,
    selectRecords,
  ])

  // One-record-one-bucket: a record with an open APWorkItem belongs to
  // Ausstehend exclusively. Strip it out of the Bereit/Inaktiv lists so it
  // doesn't render twice. The partition endpoint already enforces this for
  // its own bereit/inaktiv arrays; we mirror the rule here because the
  // legacy selection-store-driven Aktuell/Veraltet split lives one layer
  // below partition data.
  const ausstehendIds = useMemo(
    () => new Set((partition?.ausstehend ?? []).map((item) => item.record_id)),
    [partition],
  )
  const aktuellIds = useMemo(
    () =>
      allRecordIds.filter(
        (id) => selectedRecordIds.has(id) && !ausstehendIds.has(id),
      ),
    [allRecordIds, selectedRecordIds, ausstehendIds],
  )
  const veraltetIds = useMemo(
    () =>
      allRecordIds.filter(
        (id) => !selectedRecordIds.has(id) && !ausstehendIds.has(id),
      ),
    [allRecordIds, selectedRecordIds, ausstehendIds],
  )

  function getDisplayName(rec: QmsRecord): string {
    return (
      rec.values[`${config.scopeId}_name`]?.value ??
      rec.values["name"]?.value ??
      rec.values[`${config.scopeId}_id`]?.value ??
      rec.id
    )
  }

  const handleStartExport = useCallback(
    (recordId: string) => {
      // Land on the Versand-Cockpit overview; preview/composer are only seeded
      // once the user picks a channel inside the cockpit.
      setActiveExport(recordId)
      setCockpitView("cockpit")
    },
    [setActiveExport],
  )

  const handleVerwerfen = useCallback(() => {
    cancelAction()
    clearActiveExport()
    setCockpitView("cockpit")
    setComposerCtx(null)
  }, [cancelAction, clearActiveExport])

  /** Step 2a → Step 1: discard preview and return to cockpit overview. */
  const handleBackToCockpit = useCallback(() => {
    cancelAction()
    setCockpitView("cockpit")
    setComposerCtx(null)
  }, [cancelAction])

  const handleExportieren = useCallback(async () => {
    if (!activeExportRecordId) return
    setIsExporting(true)
    try {
      const { actionState } = useStore.getState()
      await config.doExport(activeExportRecordId, actionState?.diffs)
      // Log a download audit entry on the case history.
      const templateIds =
        composerCtx?.templateIds ?? getTemplates(config).map((t) => t.id)
      await addOperation({
        ap_id: config.apId,
        record_id: activeExportRecordId,
        template_ids: templateIds,
        channel: "download",
      })
      await queryClient.invalidateQueries({ queryKey: ["ap-activity"] })
      cancelAction()
      // Return to cockpit overview so the new audit entry is visible.
      setCockpitView("cockpit")
      setComposerCtx(null)
    } catch (err) {
      window.alert(
        err instanceof Error
          ? err.message
          : translate("QMS/EXPORTER/EXPORT_FAILED"),
      )
    } finally {
      setIsExporting(false)
    }
  }, [
    activeExportRecordId,
    config,
    composerCtx,
    addOperation,
    queryClient,
    cancelAction,
  ])

  /** Cockpit → Step 2b: open the email composer for a new dispatch. */
  const handleStartComposer = useCallback(
    (templateIds: string[], followUpOf?: { id: string; reason?: string }) => {
      setComposerCtx({ templateIds, followUpOf })
      setCockpitView("composer")
    },
    [],
  )

  /** Cockpit → Step 2a: open today's PDF preview (existing flow). */
  const handleStartPreview = useCallback(
    async (templateIds: string[]) => {
      if (!activeExportRecordId) return
      setComposerCtx({ templateIds })
      setCockpitView("preview")
      try {
        await config.onStartExport?.(activeExportRecordId)
      } catch {
        /* best-effort */
      }
    },
    [activeExportRecordId, config],
  )

  /** Cockpit row click → re-open the original PDF / E-Mail in read-only form. */
  const handleOpenHistoricCase = useCallback(
    (op: ExportOperation) => {
      setComposerCtx({
        templateIds:
          op.template_ids.length > 0
            ? op.template_ids
            : getTemplates(config).map((t) => t.id),
        historicOp: op,
      })
      setCockpitView(op.channel === "email" ? "composer" : "preview")
    },
    [config],
  )

  /** Composer → "E-Mail senden" — sends a real email via the backend's Resend integration. */
  const handleComposerSend = useCallback(
    async (params: {
      emailTo: string
      subject: string
      body: string
      followUpDays: number
      templateIds: string[]
      followUpOf?: string
    }) => {
      if (!activeExportRecordId) return
      let createdOp: ExportOperation | null
      try {
        createdOp = await addOperation({
          ap_id: config.apId,
          record_id: activeExportRecordId,
          template_ids: params.templateIds,
          channel: "email",
          email_to: params.emailTo,
          email_subject: params.subject,
          email_body: params.body,
          follow_up_days: params.followUpDays,
          follow_up_of: params.followUpOf,
        })
      } catch (err) {
        window.alert(
          err instanceof Error
            ? err.message
            : translate("QMS/EXPORTER/EMAIL_FAILED"),
        )
        return
      }
      // Discard the draft this send was based on, if any. The cockpit timeline
      // immediately switches to the freshly-created waiting op.
      if (composerCtx?.draftOp) removeDraft(composerCtx.draftOp.id)
      setCockpitView("cockpit")
      setComposerCtx(null)
      queryClient
        .invalidateQueries({ queryKey: ["ap-activity"] })
        .catch(() => {})

      // Demo simulation: when the AP registers an after_email_send timer and we
      // are in mock mode (no real Resend), auto-fire simulateReply after the
      // configured delay so the recording flows waiting → answered_received
      // without a manual click. See lib/ap-simulations.ts.
      const sim = getApSimulation(config.apId)
      const autoTimer = sim?.timers.find(
        (t) => t.trigger === "after_email_send",
      )
      if (
        autoTimer &&
        autoTimer.status !== "todo" &&
        integration.mode === "mock" &&
        createdOp
      ) {
        const opId = createdOp.id
        // Reveal the "Neue E-Mail erhalten — wird ausgewertet…" card almost
        // immediately after the user sends, so the detection step is plainly
        // visible long before the waiting → answered_received flip.
        const detectionLeadInMs = Math.max(0, autoTimer.delayMs - 4500)
        window.setTimeout(() => {
          setReplyDetectionOpId(opId)
        }, detectionLeadInMs)
        window.setTimeout(() => {
          setReplyDetectionOpId(null)
          simulateReplyOp(config.apId, opId).catch(() => {
            /* best-effort */
          })
        }, autoTimer.delayMs)
      }
    },
    [
      activeExportRecordId,
      config.apId,
      addOperation,
      queryClient,
      composerCtx,
      removeDraft,
      integration.mode,
      simulateReplyOp,
      setReplyDetectionOpId,
    ],
  )

  /** Cockpit → open the composer to keep editing a draft. */
  const handleEditDraft = useCallback(
    (draft: ExportOperation) => {
      setComposerCtx({
        templateIds:
          draft.template_ids.length > 0
            ? draft.template_ids
            : getTemplates(config).map((t) => t.id),
        draftOp: draft,
      })
      setCockpitView("composer")
    },
    [config],
  )

  /** Cockpit → drop a draft from the timeline. */
  const handleDiscardDraft = useCallback(
    (draft: ExportOperation) => {
      removeDraft(draft.id)
    },
    [removeDraft],
  )

  const handleToggleFromSearch = useCallback(
    (id: string) => {
      if (selectedRecordIds.has(id)) {
        deselectRecords([id])
      } else {
        selectRecords([id])
      }
    },
    [selectedRecordIds, deselectRecords, selectRecords],
  )

  const handleSwitchToFreshness = useCallback(() => {
    setExportSelectionMode("freshness")
    if (freshness) {
      clearSelection()
      if (effectiveFreshIds.length > 0) selectRecords(effectiveFreshIds)
    }
  }, [
    freshness,
    effectiveFreshIds,
    setExportSelectionMode,
    clearSelection,
    selectRecords,
  ])

  const handleQueueAccepted = useCallback(
    (recordIds: string[]) => {
      if (recordIds.length > 0) {
        selectRecords(recordIds)
        setExportSelectionMode("custom")
      }
    },
    [selectRecords, setExportSelectionMode],
  )

  // ── Export mode (cockpit always on) ─────────────────────────────────────────
  // Step 1 (Versand-Cockpit) → Step 2a (PDF preview) or Step 2b (E-Mail composer).

  if (activeExportRecordId) {
    const rec = recordById[activeExportRecordId] ?? null
    const name = rec ? getDisplayName(rec) : activeExportRecordId
    const templates = getTemplates(config)

    if (cockpitView === "composer" && composerCtx) {
      return (
        <VersandComposer
          apId={config.apId}
          apTitle={config.heading}
          recordId={activeExportRecordId}
          recordName={name}
          anchorRecord={rec}
          templateIds={composerCtx.templateIds}
          templates={templates}
          followUpOf={composerCtx.followUpOf}
          renderPreviewHtml={config.renderPreviewHtml}
          onCancel={handleBackToCockpit}
          onSend={handleComposerSend}
          historicOp={composerCtx.historicOp}
          draftOp={composerCtx.draftOp}
        />
      )
    }
    if (cockpitView === "preview") {
      return (
        <ExportPreviewView
          config={config}
          recordId={activeExportRecordId}
          recordName={name}
          onVerwerfen={handleBackToCockpit}
          onExportieren={handleExportieren}
          isExporting={isExporting}
          readOnly={!!composerCtx?.historicOp}
        />
      )
    }
    return (
      <VersandCockpit
        apId={config.apId}
        apTitle={config.heading}
        recordId={activeExportRecordId}
        recordName={name}
        recordTypeLabel={config.recordLabel.replace(/n$/, "")}
        templates={templates}
        onBack={handleVerwerfen}
        onOpenDetail={(id) => openRecordDetail(id)}
        onStartDispatch={({ kind, templateIds, followUpOf }) => {
          const followUp = followUpOf ? { id: followUpOf } : undefined
          if (kind === "email") handleStartComposer(templateIds, followUp)
          else void handleStartPreview(templateIds)
        }}
        onOpenCase={handleOpenHistoricCase}
        onEditDraft={handleEditDraft}
        onDiscardDraft={handleDiscardDraft}
      />
    )
  }

  // ── Idle mode ────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b border-grey4 px-4 pb-3 pt-4">
        <p className="mb-0.5 text-sm font-semibold text-white">
          {config.heading}
        </p>
        <p className="text-xs text-grey5">{config.subheading}</p>
      </div>

      <ApStatusTile
        apId={config.apId}
        recordIds={allRecordIds}
        recordLabel={config.recordLabel}
      />

      <div className="border-b border-grey4 px-4">
        <SearchBar
          config={config}
          records={allRecords}
          selectedIds={selectedRecordIds}
          staleIds={staleIds}
          onToggleRecord={handleToggleFromSearch}
          onSwitchToCustom={() => setExportSelectionMode("custom")}
        />

        <IntegrationQueueTray
          apId={config.apId}
          onAccepted={handleQueueAccepted}
        />

        <SelectionModeBar
          mode={exporterSelectionMode}
          threshold={exporterThreshold}
          onThresholdChange={(v) => setExportThreshold(v)}
          onSwitchToFreshness={handleSwitchToFreshness}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {partition && partition.ausstehend.length > 0 && (
          <>
            <div className="px-3 pb-1 pt-3">
              <p className="mb-1 text-xs font-semibold text-yellow1">
                <IconBolt size={11} className="-mt-px mr-1 inline" />
                {translate("QMS/EXPORTER/SECTION_AUSSTEHEND", {
                  count: partition.ausstehend.length,
                })}
              </p>
            </div>
            <div className="mx-3 mb-3 overflow-hidden rounded-lg border border-yellow1/30 bg-yellow1/3">
              {partition.ausstehend.map((item) => {
                const r = recordById[item.record_id]
                if (!r) return null
                // Prefer the partition payload's authoritative op state
                // (computed server-side at the same instant as the work
                // item), falling back to the locally-synced operations
                // store when partition has no op (e.g. monitor flag with
                // no dispatch yet).
                const opState =
                  (item.current_op_state as ExportOpState | null) ??
                  currentStateByRecord[item.record_id] ??
                  null
                return (
                  <AusstehendRow
                    key={item.id}
                    displayName={getDisplayName(r)}
                    source={item.source}
                    reason={item.reason}
                    priority={item.priority}
                    currentOpState={opState}
                    onOpen={() => handleStartExport(item.record_id)}
                  />
                )
              })}
            </div>
          </>
        )}

        <div className="px-3 pb-1 pt-3">
          <p className="text-green-400 mb-1 text-xs font-semibold">
            {translate("QMS/EXPORTER/SECTION_READY", {
              count: aktuellIds.length,
              recordLabel: config.recordLabel,
            })}
          </p>
        </div>
        <div className="mx-3 mb-3 overflow-hidden rounded-lg border border-grey4">
          {aktuellIds.length === 0 ? (
            <p className="px-3 py-3 text-xs italic text-grey5">
              {translate("QMS/EXPORTER/NO_READY", {
                recordLabel: config.recordLabel,
              })}
            </p>
          ) : (
            aktuellIds.map((id) => {
              const r = recordById[id]
              if (!r) return null
              const isStaleInCustom =
                exporterSelectionMode === "custom" && staleIds.has(id)
              return (
                <RecordRow
                  key={id}
                  rec={r}
                  displayName={getDisplayName(r)}
                  mode="aktuell"
                  isStaleInCustomMode={isStaleInCustom}
                  staleHint={null}
                  currentState={currentStateByRecord[id] ?? null}
                  onOpen={() => handleStartExport(id)}
                  onDemote={() => deselectRecords([id])}
                />
              )
            })
          )}
        </div>

        <div className="px-3 pb-1">
          <p className="text-orange-400 mb-1 text-xs font-semibold">
            {translate("QMS/EXPORTER/SECTION_INACTIVE", {
              count: veraltetIds.length,
              recordLabel: config.recordLabel,
            })}
          </p>
        </div>
        <div className="mx-3 mb-3 overflow-hidden rounded-lg border border-grey4">
          {veraltetIds.length === 0 ? (
            <p className="px-3 py-3 text-xs italic text-grey5">
              {translate("QMS/EXPORTER/NO_INACTIVE", {
                recordLabel: config.recordLabel,
              })}
            </p>
          ) : (
            veraltetIds.map((id) => {
              const r = recordById[id]
              if (!r) return null
              const baseHint =
                freshness?.stalest_relation_per_record?.[id] ?? null
              // Cascade reason takes precedence over the freshness hint —
              // "Lieferant AURUBIS AG: Lieferanten Deklaration in Bearbeitung"
              // is a stronger, more actionable signal than "Bauteil · 02.04.2024".
              const pendingReason = freshness?.pending_reason_per_record?.[id]
              const hint = pendingReason
                ? {
                    scope_id: pendingReason.source_scope_id,
                    updated_at: null,
                    demotion_reason: formatPendingReason(
                      pendingReason,
                      translate,
                    ),
                  }
                : baseHint
              return (
                <RecordRow
                  key={id}
                  rec={r}
                  displayName={getDisplayName(r)}
                  mode="veraltet"
                  staleHint={hint}
                  currentState={currentStateByRecord[id] ?? null}
                  onOpen={() => handleStartExport(id)}
                />
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
